import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';

import { ChevronLeft, ChevronRight, X, ChevronDown, ChevronUp } from 'lucide-react';

export const DRAWER_WIDTH_OPEN = 280;
export const DRAWER_WIDTH_CLOSED = 72;

const Sidebar = ({
  open,
  onToggle,
  brandTitle,
  brandSubtitle,
  brandLink,
  navItems,
}) => {

  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openItems, setOpenItems] = React.useState({});
  const handleItemClick = (label) => {
    setOpenItems(prev => ({...prev, [label]: !prev[label]}));
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="left"
      open={isMobile ? open : true}
      onClose={onToggle}
      PaperProps={{
        sx: {
          width: isMobile
            ? DRAWER_WIDTH_OPEN
            : open
            ? DRAWER_WIDTH_OPEN
            : DRAWER_WIDTH_CLOSED,

          height: "100%",

          bgcolor: "#0f172a",
          color: "#fff",

          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        },
      }}
    >

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >

        {/* Brand */}
        <Box
          sx={{
            p: open ? 2.5 : 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >

          <Link
            to={brandLink}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
            }}
          >

            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography
                component="span"
                variant="caption"
                fontWeight={800}
                sx={{ color: '#fff' }}
              >
                MM
              </Typography>
            </Box>

            {open && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: '#fff', lineHeight: 1.2 }}
                >
                  {brandTitle}
                </Typography>

                {brandSubtitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontWeight: 600,
                    }}
                  >
                    {brandSubtitle}
                  </Typography>
                )}
              </Box>
            )}

          </Link>

          {/* Mobile Close Button */}
          {isMobile && (
            <IconButton
              onClick={onToggle}
              sx={{ color: "#fff" }}
            >
              <X size={22} />
            </IconButton>
          )}

        </Box>


        {/* Navigation */}
        <List sx={{ flex: 1, py: 1.5, px: open ? 1.5 : 1 }}>

          {navItems.map(({ path, label, icon: Icon, children, onClick: navOnClick }) => {
            const hasChildren = children && children.length > 0;
            const isExpanded = openItems[label];
            const usesCustomAction = typeof navOnClick === 'function';

            // Root "/" matches only exactly — every pathname starts with "/", so prefix match would always highlight Home.
            // When path equals brandLink (e.g. Dashboard at /user), only exact match so /user/browse does not highlight Dashboard.
            // Items with navOnClick (e.g. Home → public site) skip path-based active state.
            const isActive =
              !hasChildren &&
              !usesCustomAction &&
              path &&
              (path === brandLink
                ? location.pathname === path
                : path === '/'
                  ? location.pathname === '/'
                  : location.pathname === path ||
                    location.pathname.startsWith(`${path}/`));

            const isChildActive = hasChildren && children.some(child => 
              location.pathname === child.path || location.pathname.startsWith(`${child.path}/`)
            );

            const item = (
              <ListItemButton
                key={label}
                type={usesCustomAction ? 'button' : undefined}
                component={hasChildren ? 'div' : usesCustomAction ? 'button' : Link}
                to={hasChildren || usesCustomAction ? undefined : path}
                onClick={
                  hasChildren
                    ? () => handleItemClick(label)
                    : usesCustomAction
                      ? () => {
                          navOnClick();
                          if (isMobile) onToggle();
                        }
                      : isMobile
                        ? onToggle
                        : undefined
                }
                sx={{
                  justifyContent: open ? 'flex-start' : 'center',
                  py: open ? 1.25 : 1.5,
                  px: 1.5,
                  color: "#fff",

                  '& .MuiListItemIcon-root': {
                    minWidth: open ? 40 : 32,
                    color: "#fff",
                  },

                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },

                  ...((isActive || isChildActive) && {
                    bgcolor: 'rgba(255,255,255,0.12)',
                  }),
                }}
              >

                <ListItemIcon>
                  <Icon size={open ? 20 : 22} strokeWidth={1.75} />
                </ListItemIcon>

                {(open || isMobile) && (
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: "#fff",
                    }}
                  />
                )}
                
                {hasChildren && (open || isMobile) && (
                   isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                )}

              </ListItemButton>
            );

            return (
              <React.Fragment key={label}>
                {open || isMobile ? (
                  item
                ) : (
                  <Tooltip
                    title={label}
                    placement="right"
                    arrow
                  >
                    {item}
                  </Tooltip>
                )}
                {hasChildren && (open || isMobile) && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {children.map((child) => {
                         const isChildItemActive = location.pathname === child.path || location.pathname.startsWith(`${child.path}/`);
                         return (
                           <ListItemButton
                             key={child.label}
                             component={Link}
                             to={child.path}
                             onClick={isMobile ? onToggle : undefined}
                             sx={{
                               pl: 5.5,
                               py: 1,
                               color: "rgba(255,255,255,0.7)",
                               '&:hover': {
                                 bgcolor: 'rgba(255,255,255,0.08)',
                                 color: "#fff",
                               },
                               ...(isChildItemActive && {
                                 bgcolor: 'rgba(255,255,255,0.12)',
                                 color: "#fff",
                               }),
                             }}
                           >
                             <ListItemText 
                               primary={child.label} 
                               primaryTypographyProps={{
                                 fontWeight: 500,
                                 fontSize: '0.8rem',
                               }}
                             />
                           </ListItemButton>
                         );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}

        </List>


        {/* Collapse Button Desktop */}
        {!isMobile && (
          <Box
            sx={{
              p: 1,
              borderTop: '1px solid rgba(255,255,255,0.1)',
            }}
          >

            <Tooltip
              title={open ? 'Collapse' : 'Expand'}
              placement="right"
              arrow
            >

              <IconButton
                onClick={onToggle}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {open
                  ? <ChevronLeft size={20} />
                  : <ChevronRight size={20} />
                }
              </IconButton>

            </Tooltip>

          </Box>
        )}

      </Box>

    </Drawer>
  );
};

export default Sidebar;